/**
 * Copyright (C) 2018–2020 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiBase, LuiInputBase } from '@here/lui-base'
import scopedCss from 'lui-datepicker.scss'
import template from 'lui-datepicker.html'
import Pikaday from 'pikaday'
import moment from 'moment'

const DEFAULT_LANGUAGE = 'en'

export default class LuiDatepicker extends LuiInputBase {
  static get is() {
    return 'lui-datepicker'
  }

  static get attributeToggles() {
    return [
      'disabled',
    ]
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  init() {
  }

  onConnected() {
    const types = ['text']
    if (types.indexOf(this.getAttribute('type')) === -1) {
      this.setAttribute('type', 'text')
    }
    this.delay(() => {
      this.field.value = this.field.value || this.getAttribute('value')
      this._onInputFocusBound = this._onInputFocusBound || this._onInputFocus.bind(this)
      this._onInputBlurBound = this._onInputBlurBound || this._onInputBlur.bind(this)
      this._onInputChangeEventBound = this._onInputChangeEventBound || this._onInputChangeEvent.bind(this)
      this.field.addEventListener('focus', this._onInputFocusBound)
      this.field.addEventListener('blur', this._onInputBlurBound)
      this.field.addEventListener('change', this._onInputChangeEventBound)
    })
  }

  createPicker() {
    moment.locale(this.locale || DEFAULT_LANGUAGE)

    const pikadayLocale = {
      months: moment.localeData().months(),
      weekdays: moment.localeData().weekdays(),
      weekdaysShort: moment.localeData().weekdaysShort(),
    }

    this._picker = this._picker || new Pikaday({
      field: this.field,
      container: this.shadowRoot,
      firstDay: 1,
      trigger: this,
      minDate: this.min,
      maxDate: this.max,
      format: 'YYYY-MM-DD',
      i18n: pikadayLocale,

    })
  }

  onDisconnected() {
    this.field.removeEventListener('focus', this._onInputFocusBound)
    this.field.removeEventListener('blur', this._onInputBlurBound)
    this.field.removeEventListener('change', this._onInputChangeEventBound)
  }

  disabledChanged() {
    const disabled = this.hasAttribute('disabled')
    this.toggleCssClass('lui-disabled', disabled)
    if (disabled && this._picker) {
      this._picker.destroy()
      this._picker = null
    }
  }

  get min() {
    const min = this._min || this.getAttribute('min') || (this.field ? this.field.getAttribute('min') : null)
    return min ? new Date(min + 'T00:00:00') : null
  }

  set min(value) {
    this._min = value
  }

  get max() {
    const max = this._max || this.getAttribute('max') || (this.field ? this.field.getAttribute('max') : null)
    return max ? new Date(max + 'T00:00:00') : null
  }

  set max(value) {
    this._max = value
  }

  get format() {
    return this._format || 'LL'
  }

  set format(value) {
    this._format = value
  }

  formatChanged(oldVal, newVal) {
    this.format = newVal
  }

  _valueChanged(oldVal, newVal) {
    this._value = newVal
    if (this._initialized) {
      this.field.value = newVal
    }
  }

  valueChanged(oldVal, newVal) {
    this._valueChanged(oldVal, newVal)
  }

  _getField() {
    const field = this.querySelector('input') || this._createField()
    this.constructor.attributeToggles.concat('value').forEach((attributeName) => {
      const attributeValue = this.getAttribute(attributeName)

      if (attributeValue !== null && !(attributeName === 'type')) {
        if (typeof field[attributeName] !== 'undefined') {
          // Prefer to set in JS if possible; convert values for JS if needed:
          // TODO LUI-1045: Extract React specific behavior (unnecessary check for 'false' value)
          field[attributeName] = LuiBase.isBooleanAttribute(attributeName) ? (attributeValue !== 'false') : attributeValue
        } else {
          // Fallback if attribute has no JS equivalent:
          field.setAttribute(attributeName, attributeValue)
        }
      }
    })
    field.setAttribute('type', 'text')
    this._addPropertyListenerToField(field)

    this.seedElement = field
    return field
  }

  _addPropertyListenerToField(field) {
    const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(
      field)).value
    if (!nativeImplementation) return
    Object.defineProperty(field, 'value', {
      set: (date) => {
        let formattedDate
        let parsedDate
        if (typeof date !== 'string') {
          nativeImplementation.set.call(field, '')
        } else {
          if (date.match(/\d{4}-\d{2}-\d{2}/)) {
            parsedDate = moment(date, 'YYYY-MM-DD')
            this._value = date
          } else {
            parsedDate = moment(date, this.format)
          }
          if (parsedDate.isValid()) {
            formattedDate = parsedDate.locale(this.locale).format(this.format)
            nativeImplementation.set.call(field, formattedDate)
          }
        }
      },
      get: () => {
        const formattedDate = nativeImplementation.get.call(field)
        if (formattedDate === '') return ''
        let format = this.format
        if (formattedDate.match(/\d{4}-\d{2}-\d{2}/)) {
          format = 'YYYY-MM-DD'
        } else if (moment.localeData(this.locale)._longDateFormat[format]) {
          format = moment.localeData(this.locale)._longDateFormat[format]
        }
        moment.locale(this.locale)
        return moment(formattedDate, format).format('YYYY-MM-DD')
      },
    })
  }

  get locale() {
    const langAttribute = this.closest('[lang]')
    this._locale = this._locale || (langAttribute && langAttribute.getAttribute('lang')) || DEFAULT_LANGUAGE
    return this._locale
  }

  _createField() {
    const element = document.createElement('input')
    element.type = 'text'
    this.wrapper.appendChild(element)
    return element
  }

  get wrapper() {
    return this._wrapper || (this._wrapper = this.shadowRoot.querySelector('.lui-datepicker__wrapper'))
  }

  get value() {
    return this.field ? this.field.value : this.getAttribute('value')
  }

  set value(newValue) {
    this.setAttribute('value', newValue)
    this.valueChanged('', newValue)
  }

  _onInputFocus() {
    this.createPicker()
    this.classList.add('lui-focus')
  }

  _onInputBlur() {
    this.classList.remove('lui-focus')
    this.setAttribute('value', this.field.value)
  }

  _onInputChangeEvent() {
    this.field.dispatchEvent(new window.InputEvent('input'))
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-datepicker/lui-datepicker.js
// module id = 197
// module chunks = 0 1 2